import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAltHotkeys } from '../../../hooks/useAltHotkeys';
import { useModalDrag } from '../../../hooks/useModalDrag';
import ModalHeader from '../../../components/ui/ModalHeader';
import { CalculatorIcon } from '@heroicons/react/24/outline';
import Row from '../../pro/components/orderform/Row';
import { Label } from '../../pro/components/orderform/Label';
import TextInput from '../../pro/components/orderform/TextInput';
import SelectInput, { type SelectOption } from '../../pro/components/orderform/SelectInput';
import FieldValue from './FieldValue';
import DrayRateHint from './DrayRateHint';
import BillToLookupField from './BillToLookupField';
import FscTariffLookupModal from './FscTariffLookupModal';
import { fscRateUnit, FSC_EDITABLE_TYPES } from './useQuoteFsc';
import { DELIVERY_ZONES, FSC_TYPES } from '../quoteOptions';
import quoteService, {
  type DrayRate, type FscResult, type FscTariff,
} from '../../../services/quote.service';
import type { BillToLookupRow } from '../fixtures';
import { getRateZones } from '../../../services/orderLookups.service';

const LBL = 118;

interface Props {
  open: boolean;
  onClose: () => void;
}

/** FSC types that carry no surcharge at all — nothing to resolve, no call. */
const NO_FSC_TYPES = new Set(['', 'FLAT']);

const money = (n: number) => `$${n.toFixed(2)}`;

/**
 * Standalone drayage-rate calculator (read-only).
 *
 * The quote team used to create a mock quote, save it to trigger the
 * calculation, read the Rate off the Drayage & Fuel card, and throw the quote
 * away. This asks the same three endpoints the quote form asks — /quotes/
 * calculate-miles, /quotes/dray-rate, /quotes/fsc — and just shows the answer.
 * Nothing is written.
 *
 * It deliberately calls `quoteService` directly rather than reusing
 * `useQuoteDeliveryGeo` / `useQuoteFsc`: both hooks skip their first run so that
 * opening a saved quote can never overwrite its stored miles / rate / FSC. Here
 * there are no stored values and the first computation is the only one that
 * matters, so that guard is exactly backwards.
 */
const RateCalculatorModal: React.FC<Props> = ({ open, onClose }) => {
  // ── Inputs ──────────────────────────────────────────────────────────────
  const [rateZone, setRateZone] = useState('');
  const [deliveryZone, setDeliveryZone] = useState('LOCAL');
  const [zip, setZip] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [billToName, setBillToName] = useState('');
  const [billToCode, setBillToCode] = useState(0);
  const [fscType, setFscType] = useState('');
  const [fscRateInput, setFscRateInput] = useState('');
  const [tariffNumber, setTariffNumber] = useState('');

  // ── Results ─────────────────────────────────────────────────────────────
  const [miles, setMiles] = useState<number | null>(null);
  const [milesError, setMilesError] = useState<string | null>(null);
  const [dray, setDray] = useState<DrayRate | null>(null);
  const [fsc, setFsc] = useState<FscResult | null>(null);
  const [fscNotice, setFscNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // ── Lookups ─────────────────────────────────────────────────────────────
  const [zoneOptions, setZoneOptions] = useState<SelectOption[]>([]);
  const [tariffInfo, setTariffInfo] = useState<FscTariff | null>(null);
  const [tariffError, setTariffError] = useState<string | null>(null);
  const [tariffLookupOpen, setTariffLookupOpen] = useState(false);
  const [geoBusy, setGeoBusy] = useState(false);
  const geoRequest = useRef(0);
  const calculationRequest = useRef(0);

  useEffect(() => () => {
    geoRequest.current += 1;
    calculationRequest.current += 1;
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    getRateZones()
      .then((rows) => {
        if (cancelled) return;
        setZoneOptions(rows.map((r) => ({ value: String(r.value), label: r.label })));
      })
      .catch(() => { /* non-fatal — the zone select just stays empty */ });
    return () => { cancelled = true; };
  }, [open]);

  const clearResults = useCallback(() => {
    calculationRequest.current += 1;
    setBusy(false);
    setMiles(null);
    setMilesError(null);
    setDray(null);
    setFsc(null);
    setFscNotice(null);
  }, []);

  // Any input change invalidates the last answer. Numbers left on screen next to
  // changed inputs would be read as belonging to the new lane — worse than blank.
  useEffect(() => {
    clearResults();
  }, [rateZone, deliveryZone, zip, city, state, fscType, fscRateInput, tariffNumber, clearResults]);

  // Reopening starts clean rather than resuming someone else's last lane.
  useEffect(() => {
    if (open) return;
    setRateZone(''); setDeliveryZone('LOCAL');
    setZip(''); setCity(''); setState('');
    setBillToName(''); setBillToCode(0);
    setFscType(''); setFscRateInput(''); setTariffNumber('');
    setTariffInfo(null); setTariffError(null);
    setGeoBusy(false);
    clearResults();
  }, [open, clearResults]);

  const z5 = zip.trim().slice(0, 5);
  const hasDestination = z5.length === 5 || (!!city.trim() && !!state.trim());
  const canCalculate = !!rateZone && hasDestination && !busy && !geoBusy;

  // Editing a destination invalidates any outstanding lookup. A new ZIP must
  // not travel to Google with the previous ZIP's city/state: Google can ignore
  // the ZIP and route to that old city instead.
  const changeDestination = (field: 'zip' | 'city' | 'state', value: string) => {
    geoRequest.current += 1;
    setGeoBusy(false);
    clearResults();
    if (field === 'zip') {
      setZip(value);
      setCity('');
      setState('');
    } else if (field === 'city') {
      setCity(value);
    } else {
      setState(value);
    }
  };

  // ZIP -> city/state and city+state -> ZIP, same as the quote form's delivery
  // fields (V1 inline_quote.pl getcitystatezip). Non-fatal: type it by hand.
  const handleZipBlur = () => {
    if (z5.length !== 5) return;
    const request = ++geoRequest.current;
    setGeoBusy(true);
    quoteService.geocodeCityStateZip(z5)
      .then((r) => {
        if (request !== geoRequest.current) return;
        if (r?.city) setCity(r.city);
        if (r?.state) setState(r.state);
      })
      .catch(() => { /* non-fatal */ })
      .finally(() => { if (request === geoRequest.current) setGeoBusy(false); });
  };

  const handleCityStateBlur = () => {
    const c = city.trim();
    const s = state.trim();
    if (!c || !s || z5.length === 5) return;
    const request = ++geoRequest.current;
    setGeoBusy(true);
    quoteService.geocodeCityStateZip(`${c}, ${s}`)
      .then((r) => { if (request === geoRequest.current && r?.zip) setZip(r.zip); })
      .catch(() => { /* non-fatal */ })
      .finally(() => { if (request === geoRequest.current) setGeoBusy(false); });
  };

  // Resolve a hand-typed tariff number so the user can confirm they got the one
  // they meant — a wrong number would otherwise silently price the wrong lane.
  const handleTariffBlur = () => {
    const n = parseInt(tariffNumber.trim(), 10) || 0;
    setTariffInfo(null);
    setTariffError(null);
    if (!n) return;
    quoteService.findFscTariff(n)
      .then((t) => {
        if (t) setTariffInfo(t);
        else setTariffError(`Tariff ${n} not found.`);
      })
      .catch(() => setTariffError('Could not look up that tariff.'));
  };

  const pickTariff = (t: FscTariff) => {
    setTariffNumber(String(t.tariffNumber));
    setTariffInfo(t);
    setTariffError(null);
  };

  const fscEditable = FSC_EDITABLE_TYPES.has(fscType);
  const tariffNum = parseInt(tariffNumber.trim(), 10) || 0;

  /**
   * Strictly sequential: miles feed the mileage bands, and the dray rate is the
   * basis a percentage FSC applies to. Each step is skipped when the previous
   * one produced nothing meaningful to build on.
   */
  const calculate = async () => {
    if (!canCalculate) return;
    clearResults();
    const request = calculationRequest.current;
    setBusy(true);
    try {
      const c = city.trim();
      const s = state.trim();
      const m = await quoteService.calculateQuoteMiles({
        ratezone: rateZone,
        deliveryzone: deliveryZone,
        zip: z5 || undefined,
        city: c || undefined,
        state: s || undefined,
      });
      if (request !== calculationRequest.current) return;
      if (m.miles == null) {
        setMilesError(m.error || 'Could not calculate miles.');
        return; // no miles -> no band and no basis; a rate here would be a guess
      }
      setMiles(m.miles);

      const d = await quoteService.getDrayRate({
        ratezone: rateZone, zip: z5 || undefined, deliveryzone: deliveryZone, miles: m.miles,
      });
      if (request !== calculationRequest.current) return;
      setDray(d);
      // A LOCAL standards zone with no (zone, zip) pairing has no rate to give.
      // That IS the answer, so stop here rather than pricing fuel on nothing.
      if (d.notDefined || d.drayRate == null) return;

      if (NO_FSC_TYPES.has(fscType)) return;
      if (fscType === 'CUSTOMER' && !tariffNum) {
        setFscNotice('Pick a customer tariff to include fuel.');
        return;
      }
      const fuel = await quoteService.getFsc({
        fsctype: fscType,
        fsctariff: tariffNum || undefined,
        deliveryzone: deliveryZone,
        fscrate: parseFloat(fscRateInput) || 0,
        dray: d.drayRate,
        miles: m.miles,
      });
      if (request === calculationRequest.current) setFsc(fuel);
    } catch {
      if (request === calculationRequest.current) setMilesError('Could not calculate. Try again.');
    } finally {
      if (request === calculationRequest.current) setBusy(false);
    }
  };

  // The FSC rate to show: resolved for CURRENT / CUSTOMER, the typed value for
  // the user-entered types (the endpoint returns null for those by design).
  const shownFscRate = useMemo(() => {
    if (fsc?.fscRate != null) return fsc.fscRate;
    if (fsc && fscEditable) return parseFloat(fscRateInput) || 0;
    return null;
  }, [fsc, fscEditable, fscRateInput]);

  const fscUnit = fscRateUnit(fscType, fsc?.ratetype ?? tariffInfo?.ratetype ?? null);
  const ratePerMile = dray?.drayRate != null && miles ? `${money(dray.drayRate / miles)} / mile` : '';

  // Pricing scratchpad — nothing to persist, so Alt+C only. The layer keeps
  // Alt+S off the quote drawer behind it.
  useAltHotkeys({ C: onClose }, undefined, { layer: open });

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(open);

  if (!open) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-start justify-center overflow-y-auto bg-slate-900/40 p-6"
      role="dialog"
      aria-label="Rate Calculator"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        style={panelStyle}
        className="w-full max-w-[720px] rounded-lg border border-slate-200 bg-panel shadow-xl dark:border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader
          title={
            <span className="flex items-center gap-2">
              <CalculatorIcon className="h-4 w-4 text-blue-300" />
              Rate Calculator
            </span>
          }
          onClose={onClose}
          dragHandleProps={dragHandleProps}
        />

        <div className="px-4 py-3">
          <p className="mb-3 text-[0.71875rem] text-slate-500 dark:text-slate-400">
            Prices a lane without creating a quote. Nothing here is saved.
          </p>

          <div className="grid grid-cols-1 gap-x-6 sm:grid-cols-2">
            {/* ── Lane ───────────────────────────────────────────────── */}
            <div>
              <div className="mb-1.5 text-[0.65625rem] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Lane
              </div>
              <Row>
                <Label width={LBL}>Rate Zone</Label>
                <SelectInput
                  value={rateZone}
                  onChange={setRateZone}
                  options={zoneOptions}
                  ariaLabel="Rate Zone"
                  placeholder="—"
                />
              </Row>
              <Row>
                <Label width={LBL}>Delivery Zone</Label>
                <SelectInput
                  value={deliveryZone}
                  onChange={setDeliveryZone}
                  options={DELIVERY_ZONES}
                  ariaLabel="Delivery Zone"
                  width={110}
                />
              </Row>
              <Row>
                <Label width={LBL}>Deliver-To Zip</Label>
                <TextInput
                  value={zip}
                  onChange={(v) => changeDestination('zip', v)}
                  onBlur={handleZipBlur}
                  ariaLabel="Deliver-To Zip"
                  width={96}
                  placeholder="—"
                />
                {geoBusy && <span className="text-[0.6875rem] text-slate-400 dark:text-slate-500">Looking up…</span>}
              </Row>
              <Row>
                <Label width={LBL}>City / State</Label>
                <TextInput value={city} onChange={(v) => changeDestination('city', v)} onBlur={handleCityStateBlur} ariaLabel="Deliver-To City" />
                <TextInput value={state} onChange={(v) => changeDestination('state', v)} onBlur={handleCityStateBlur} ariaLabel="Deliver-To State" width={48} />
              </Row>
            </div>

            {/* ── Fuel ───────────────────────────────────────────────── */}
            <div>
              <div className="mb-1.5 text-[0.65625rem] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Fuel <span className="font-normal normal-case tracking-normal text-slate-400">(optional)</span>
              </div>
              <Row>
                <Label width={LBL}>Bill-To</Label>
                <BillToLookupField
                  value={billToName}
                  linkedCode={billToCode}
                  onType={(name) => { setBillToName(name); setBillToCode(0); }}
                  onPick={(r: BillToLookupRow) => { setBillToName(r.billtoname); setBillToCode(r.billto); }}
                  ariaLabel="Bill-To"
                  placeholder="Optional"
                />
              </Row>
              <Row>
                <Label width={LBL}>Fuel Type</Label>
                <SelectInput
                  value={fscType}
                  onChange={(v) => { setFscType(v); setFscRateInput(''); }}
                  options={['', ...FSC_TYPES]}
                  ariaLabel="Fuel Type"
                  width={130}
                  placeholder="—"
                />
              </Row>
              {fscType === 'CUSTOMER' && (
                <>
                  <Row>
                    <Label width={LBL}>Cust Tariff #</Label>
                    <TextInput
                      value={tariffNumber}
                      onChange={setTariffNumber}
                      onBlur={handleTariffBlur}
                      ariaLabel="Cust Tariff #"
                      width={90}
                      placeholder="—"
                    />
                    {/* Enabled with no bill-to on purpose — that's the generic list. */}
                    <button
                      type="button"
                      onClick={() => setTariffLookupOpen(true)}
                      title={billToCode > 0 ? "Look up this bill-to's FSC tariffs" : 'Look up any FSC tariff'}
                      className="text-[0.71875rem] font-semibold text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                    >
                      Lookup
                    </button>
                  </Row>
                  {tariffInfo && (
                    <div className="text-[0.6875rem] text-slate-500 dark:text-slate-400" style={{ paddingLeft: LBL + 8 }}>
                      {[
                        tariffInfo.name,
                        tariffInfo.tariffType,
                        tariffInfo.ratetype,
                        tariffInfo.effectiveDate ? `eff ${tariffInfo.effectiveDate}` : '',
                      ].filter(Boolean).join(' · ')}
                    </div>
                  )}
                  {tariffError && (
                    <div className="text-[0.6875rem] text-amber-600 dark:text-amber-400" style={{ paddingLeft: LBL + 8 }}>
                      {tariffError}
                    </div>
                  )}
                </>
              )}
              {fscEditable && (
                <Row>
                  <Label width={LBL}>FSC Rate</Label>
                  <TextInput
                    value={fscRateInput}
                    onChange={setFscRateInput}
                    ariaLabel="FSC Rate"
                    width={96}
                    placeholder="—"
                  />
                  <span className="w-14 text-[0.6875rem] text-slate-500 dark:text-slate-400">{fscUnit}</span>
                </Row>
              )}
            </div>
          </div>

          <div className="mt-3 flex items-center gap-3 border-t border-slate-200 pt-3 dark:border-gray-700">
            <button
              type="button"
              onClick={calculate}
              disabled={!canCalculate}
              className="rounded-md border border-blue-700 bg-blue-600 px-3.5 py-2 text-[0.78125rem] font-semibold text-white shadow-sm hover:bg-blue-700 disabled:cursor-default disabled:border-slate-300 disabled:bg-slate-300 dark:disabled:border-slate-600 dark:disabled:bg-slate-600"
            >
              Calculate
            </button>
            {busy && <span className="text-[0.71875rem] text-slate-400 dark:text-slate-500">Calculating…</span>}
            {!rateZone && <span className="text-[0.71875rem] text-slate-400 dark:text-slate-500">Pick a rate zone.</span>}
            {!!rateZone && !hasDestination && (
              <span className="text-[0.71875rem] text-slate-400 dark:text-slate-500">Enter a delivery ZIP, or city + state.</span>
            )}
          </div>

          {/* ── Results ─────────────────────────────────────────────── */}
          <div className="mt-3 rounded border border-slate-200 bg-slate-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-900/40">
            <Row>
              <Label width={LBL}>Miles</Label>
              <FieldValue value={miles == null ? '' : String(miles)} ariaLabel="Miles" width={96} />
            </Row>
            {milesError && (
              <div className="text-[0.71875rem] text-amber-600 dark:text-amber-400" style={{ paddingLeft: LBL + 8 }}>
                {milesError}
              </div>
            )}
            <Row>
              <Label width={LBL}>Rate</Label>
              <FieldValue
                value={dray?.drayRate == null ? '' : money(dray.drayRate)}
                ariaLabel="Rate"
                width={110}
              />
              {ratePerMile && <span className="text-[0.6875rem] text-slate-400 dark:text-slate-500">{ratePerMile}</span>}
            </Row>
            {/* The standard / minimum the quoted rate is measured against. */}
            <div style={{ paddingLeft: LBL + 8 }}>
              <DrayRateHint info={dray} drayRate={dray?.drayRate ?? 0} miles={miles ?? 0} />
            </div>
            {dray?.notDefined && (
              <div className="text-[0.71875rem] text-amber-600 dark:text-amber-400" style={{ paddingLeft: LBL + 8 }}>
                {dray.message || `Delivery rate is not defined for ${rateZone} to ${z5}.`}
              </div>
            )}
            <Row>
              <Label width={LBL}>FSC</Label>
              <FieldValue
                value={shownFscRate == null ? '' : String(shownFscRate)}
                ariaLabel="FSC"
                width={96}
              />
              {shownFscRate != null && fscUnit && (
                <span className="text-[0.6875rem] text-slate-500 dark:text-slate-400">{fscUnit}</span>
              )}
              {/* The dollars the surcharge actually adds to this dray. */}
              {fsc && fsc.adjustment > 0 && (
                <span className="text-[0.6875rem] text-slate-500 dark:text-slate-400">= {money(fsc.adjustment)}</span>
              )}
            </Row>
            {fscNotice && (
              <div className="text-[0.71875rem] text-amber-600 dark:text-amber-400" style={{ paddingLeft: LBL + 8 }}>
                {fscNotice}
              </div>
            )}
            {dray?.drayRate != null && fsc && (
              <Row>
                <Label width={LBL}>Dray + Fuel</Label>
                <FieldValue
                  value={money(dray.drayRate + (fsc.adjustment || 0))}
                  ariaLabel="Dray plus Fuel"
                  width={110}
                />
              </Row>
            )}
          </div>
        </div>
      </div>

      <FscTariffLookupModal
        open={tariffLookupOpen}
        billto={billToCode}
        generic={billToCode <= 0}
        onClose={() => setTariffLookupOpen(false)}
        onPick={pickTariff}
      />
    </div>,
    document.body,
  );
};

export default RateCalculatorModal;
